import React from "react";
import { ClickAwayListener, IconButton, Tooltip, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * Tappable info icon with a descriptive bubble — the mobile counterpart of the
 * desktop `components/InfoIconWithTooltip`.
 *
 * The bubble is styled like desktop's `BootstrapTooltip` (white, thin dark
 * border, arrow), but every hover/focus/long-press trigger is disabled and the
 * open state is driven by a tap instead: on touch there is no hover, so the
 * desktop icon is effectively dead on a phone. Tapping again — or anywhere
 * outside — closes it.
 */
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.shades["clr-white-900"],
    "&::before": {
      border: "1px solid",
      borderColor: theme.palette.neutral["clr-700"],
    },
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.shades["clr-white-900"],
    color: theme.palette.shades["clr-black-900"],
    boxShadow: "0px 0px 4px 0px #00000029",
    border: "1px solid",
    borderColor: theme.palette.neutral["clr-700"],
    padding: 10,
    maxWidth: 260,
    ...theme.typography["body02-medium"],
  },
}));

export default function InfoTooltip({
  title,
  placement = "bottom",
  fontSize = 13,
}) {
  const [open, setOpen] = React.useState(false);

  if (!title) return null;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      {/* Tooltip needs a ref-holding child, and ClickAwayListener needs one too. */}
      <span style={{ display: "inline-flex" }}>
        <StyledTooltip
          title={title}
          placement={placement}
          open={open}
          onClose={() => setOpen(false)}
          disableHoverListener
          disableFocusListener
          disableTouchListener
        >
          <IconButton
            aria-label="More information"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((current) => !current);
            }}
            sx={{ p: 0.25 }}
          >
            <InfoOutlinedIcon sx={{ fontSize, color: "neutral.clr-400" }} />
          </IconButton>
        </StyledTooltip>
      </span>
    </ClickAwayListener>
  );
}
